//the KPI data report (PLAN-064): every measurement of the selected KPIs in a period, as Excel or JSON, built by a
//background job; the file lives 72 hours and is downloaded through a short-lived link
export enum KPIDataReportFormat {
  Json = 1,
  Excel = 2,
}

export enum KPIDataReportJobStatus {
  Queued = 1,
  Running = 2,
  Done = 3,
  Failed = 4,
}

export class KPIDataReportEstimate {
  points: number;

  evidenceRecords: number;

  evidenceBytes: number;

  constructor() {
    this.points = 0;
    this.evidenceRecords = 0;
    this.evidenceBytes = 0;
  }
}

export class KPIDataReportJob {
  jobId: number;

  status: KPIDataReportJobStatus;

  format: KPIDataReportFormat;

  kpiIds: number[];

  start?: Date;

  end?: Date;

  created?: Date;

  started?: Date;

  completed?: Date;

  //1 runs next in the organization; 0 when not queued
  queuePosition: number;

  rowsWritten: number;

  //the estimate at queue time; undefined when unknown
  rowsTotal?: number;

  evidenceRows: number;

  fileName?: string;

  bytes?: number;

  error?: string;

  omittedKpiIds: number[];

  //the file is gone
  expired: boolean;

  //the library item the file was saved as (PLAN-069); undefined while not saved
  savedLinkId?: number;

  savedListId?: number;

  savedFolderName?: string;

  constructor() {
    this.jobId = 0;
    this.status = KPIDataReportJobStatus.Queued;
    this.format = KPIDataReportFormat.Excel;
    this.kpiIds = [];
    this.queuePosition = 0;
    this.rowsWritten = 0;
    this.evidenceRows = 0;
    this.omittedKpiIds = [];
    this.expired = false;
  }

  //the file can be downloaded
  isReady(): boolean {
    return this.status === KPIDataReportJobStatus.Done && !this.expired;
  }

  //0 to 1; 0 while the total is unknown
  getProgress(): number {
    if (this.status === KPIDataReportJobStatus.Done) return 1;
    if (!this.rowsTotal) return 0;

    return Math.min(1, this.rowsWritten / this.rowsTotal);
  }

  isEqual(item: KPIDataReportJob): boolean {
    return (
      this.jobId === item.jobId &&
      this.status === item.status &&
      this.queuePosition === item.queuePosition &&
      this.rowsWritten === item.rowsWritten &&
      this.rowsTotal === item.rowsTotal &&
      this.fileName === item.fileName &&
      this.error === item.error &&
      this.expired === item.expired &&
      this.savedLinkId === item.savedLinkId &&
      this.savedListId === item.savedListId &&
      this.savedFolderName === item.savedFolderName
    );
  }

  clone(): KPIDataReportJob {
    const output = new KPIDataReportJob();
    output.jobId = this.jobId;
    output.status = this.status;
    output.format = this.format;
    output.kpiIds = [...this.kpiIds];
    output.start = this.start;
    output.end = this.end;
    output.created = this.created;
    output.started = this.started;
    output.completed = this.completed;
    output.queuePosition = this.queuePosition;
    output.rowsWritten = this.rowsWritten;
    output.rowsTotal = this.rowsTotal;
    output.evidenceRows = this.evidenceRows;
    output.fileName = this.fileName;
    output.bytes = this.bytes;
    output.error = this.error;
    output.omittedKpiIds = [...this.omittedKpiIds];
    output.expired = this.expired;
    output.savedLinkId = this.savedLinkId;
    output.savedListId = this.savedListId;
    output.savedFolderName = this.savedFolderName;

    return output;
  }
}

export class KPIDataReportDownload {
  url: string;

  fileName: string;

  expiresOn?: Date;

  constructor() {
    this.url = '';
    this.fileName = '';
  }
}
